import { generateBybitSignature } from './bybit-signature';

// Get Bybit balance with better error handling
export async function getBybitBalance(apiKey: string, apiSecret: string, coin: string): Promise<number> {
  try {
    const timestamp = Date.now().toString();
    const params = { 
      accountType: 'UNIFIED',
      coin 
    };
    
    // Generate signature
    const signature = await generateBybitSignature(params, apiSecret);

    // Make API request with proper headers and query string
    const response = await fetch(`https://api.bybit.com/v5/account/wallet-balance?accountType=UNIFIED&coin=${coin}`, {
      method: 'GET',
      headers: {
        'X-BAPI-API-KEY': apiKey,
        'X-BAPI-SIGN': signature,
        'X-BAPI-TIMESTAMP': timestamp,
        'X-BAPI-RECV-WINDOW': '5000',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.retMsg || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Check API response code
    if (data.retCode !== 0) {
      throw new Error(data.retMsg || 'Failed to get balance');
    }

    // Safely extract balance with fallbacks
    const balance = data.result?.list?.[0]?.coin?.find(c => c.coin === coin)?.walletBalance || '0';
    return parseFloat(balance);
  } catch (error) {
    if (error instanceof Error) {
      console.error('Bybit balance error:', error.message);
    } else {
      console.error('Bybit balance error:', error);
    }
    return 0;
  }
}

// Get Bybit positions with better error handling
export async function getBybitPositions(apiKey: string, apiSecret: string, symbol?: string): Promise<any[]> {
  try {
    const timestamp = Date.now().toString();
    const params = { 
      category: 'linear',
      settleCoin: 'USDT'
    };
    if (symbol) {
      params.symbol = symbol;
    }
    
    const signature = await generateBybitSignature(params, apiSecret);

    // Make API request with proper headers
    const response = await fetch(`https://api.bybit.com/v5/position/list?category=linear&settleCoin=USDT${symbol ? `&symbol=${symbol}` : ''}`, {
      method: 'GET',
      headers: {
        'X-BAPI-API-KEY': apiKey,
        'X-BAPI-SIGN': signature,
        'X-BAPI-TIMESTAMP': timestamp,
        'X-BAPI-RECV-WINDOW': '5000',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.retMsg || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (data.retCode !== 0) {
      throw new Error(data.retMsg || 'Failed to get positions');
    }

    // Filter out empty positions and format data
    return (data.result?.list || []).filter(pos => parseFloat(pos.size) > 0).map(pos => ({
      symbol: pos.symbol,
      side: pos.side,
      size: parseFloat(pos.size),
      entryPrice: parseFloat(pos.entryPrice),
      markPrice: parseFloat(pos.markPrice),
      unrealizedPnl: parseFloat(pos.unrealisedPnl),
      leverage: parseFloat(pos.leverage),
      createdTime: pos.createdTime
    }));
  } catch (error) {
    if (error instanceof Error) {
      console.error('Bybit positions error:', error.message);
    } else {
      console.error('Bybit positions error:', error);
    }
    return [];
  }
}

// Place Bybit order with better error handling
export async function placeBybitOrder(
  apiKey: string,
  apiSecret: string,
  orderParams: any
): Promise<any> {
  try {
    const timestamp = Date.now().toString();
    
    // Generate signature
    const signature = await generateBybitSignature(orderParams, apiSecret);

    // Send order with proper headers
    const response = await fetch('https://api.bybit.com/v5/order/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-BAPI-API-KEY': apiKey,
        'X-BAPI-SIGN': signature,
        'X-BAPI-TIMESTAMP': timestamp,
        'X-BAPI-RECV-WINDOW': '5000'
      },
      body: JSON.stringify(orderParams)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.retMsg || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.retCode !== 0) {
      throw new Error(result.retMsg || 'Failed to place order');
    }

    return result.result;
  } catch (error) {
    console.error('Error placing Bybit order:', error);
    throw error;
  }
}
